// Mapa_Veiculos.jsx
// Mapa em tempo real da frota (ObterPosicaoVeiculo)
//
// Requisitos:
//   - @mui/material
//   - react-leaflet
//   - leaflet
//   - (opcional) @mui/icons-material para ícones de busca/refresh

import * as React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  Button,
  ButtonGroup,
  Popover,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import RoomIcon from '@mui/icons-material/Room';
import { useTheme } from '@mui/material/styles';

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip as LeafletTooltip,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// ===== Config de ambiente (igual Setor3Page) =====
const API_BASE =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';

const API_KEY =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_API_KEY) ||
  process.env.REACT_APP_API_KEY ||
  '';

// ===== Constantes =====
const REFRESH_SECONDS = 30;

// Coordenadas aproximadas de Manaus para centro inicial
const MANAUS_CENTER = {
  lat: -3.019545,
  lng: -60.026674,
};

// Ícone de ônibus usando HTML: azul (em movimento) e vermelho (parado)
function createBusIcon(isMoving) {
  const bgColor = isMoving ? '#1976d2' : '#d32f2f'; // azul / vermelho

  return L.divIcon({
    className: 'custom-bus-icon',
    html: `
      <div style="
        background:${bgColor};
        color:white;
        width:28px;
        height:28px;
        border-radius:50%;
        display:flex;
        align-items:center;
        justify-content:center;
        border:2px solid white;
        box-shadow:0 0 4px rgba(0,0,0,0.5);
        font-size:16px;
      ">
        🚍
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
}


// ===== Helpers =====
const norm = (v) => (v ?? '').toString().trim();
const normCode = (v) => (v ?? '').toString().trim().toUpperCase();


function formatDateTimeBR(ms) {
  if (!ms) return '';
  try {
    const d = new Date(Number(ms));
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return '';
  }
}

// Normaliza o objeto vindo do ObterPosicaoVeiculo
function normalizeVeiculo(raw) {
  const lat = Number(raw.latitude);
  const lng = Number(raw.longitude);

  return {
    raw,
    codigo: norm(raw.codigo),
    placa: norm(raw.placa),
    linha: norm(raw.linha),
    trajeto: norm(raw.trajeto),
    sentido: norm(raw.sentido),
    velocidade: Number(raw.velocidade) || 0,
    ignicao: Number(raw.ignicao) || 0,
    hodometro: Number(raw.hodometro) || 0,
    direcao: Number(raw.direcao) || 0,
    dataHora: raw.dataHora,
    latitude: Number.isFinite(lat) ? lat : null,
    longitude: Number.isFinite(lng) ? lng : null,
  };
}

// Distância aproximada em metros entre duas coordenadas (Haversine)
function distanceMeters(lat1, lng1, lat2, lng2) {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371000; // raio da Terra em metros
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Encontra o "centro" da garagem como o ponto com mais vizinhos parados em até 250m
function findGarageCenter(veiculos) {
  const parados = veiculos.filter(
    (v) =>
      v.latitude != null &&
      v.longitude != null &&
      v.velocidade === 0
  );

  if (parados.length === 0) return null;

  let best = null;
  let bestCount = 0;

  parados.forEach((v) => {
    let count = 0;
    parados.forEach((u) => {
      const d = distanceMeters(
        v.latitude,
        v.longitude,
        u.latitude,
        u.longitude
      );
      if (d <= 250) {
        count += 1;
      }
    });

    if (count > bestCount) {
      bestCount = count;
      best = v;
    }
  });

  return best
    ? { lat: best.latitude, lng: best.longitude }
    : null;
}

// Busca o array de veículos da API de tráfego
async function fetchPosicoesVeiculos({ signal } = {}) {
  if (!API_BASE) {
    throw new Error(
      'API_BASE não configurada. Verifique VITE_API_BASE ou REACT_APP_API_BASE.'
    );
  }

  const url = `${API_BASE}/api/trafego/posicao-veiculo`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: API_KEY,
      'x-api-key': API_KEY,
    },
    signal,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      `Falha ao carregar posição de veículos (${res.status}) — ${text}`
    );
  }

  const data = await res.json();

  let veiculos = [];
  if (Array.isArray(data)) {
    veiculos = data;
  } else if (data && Array.isArray(data.veiculos)) {
    veiculos = data.veiculos;
  }

  return veiculos
    .map(normalizeVeiculo)
    .filter((v) => v.latitude != null && v.longitude != null);
}

// Busca serviços de manutenção (O.S) e retorna apenas O.S em aberto dos últimos 30 dias
async function fetchServicosAbertos({ signal } = {}) {
  if (!API_BASE) {
    throw new Error(
      'API_BASE não configurada. Verifique VITE_API_BASE ou REACT_APP_API_BASE.'
    );
  }

  const hoje = new Date();
  const fim = hoje;
  const inicio = new Date(hoje);
  inicio.setDate(inicio.getDate() - 30); // últimos 30 dias

  const formatBR = (d) =>
    d.toLocaleDateString('pt-BR', { timeZone: 'America/Manaus' });

  const sp = new URLSearchParams();
  sp.set('startDate', formatBR(inicio));
  sp.set('endDate', formatBR(fim));

  const url = `${API_BASE}/api/setor3/servicos?${sp.toString()}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: API_KEY,
      'x-api-key': API_KEY,
    },
    signal,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      `Falha ao carregar serviços (${res.status}) — ${text}`
    );
  }

  const data = await res.json();
  const rows = Array.isArray(data) ? data : data.rows || data.servicos || [];

  // Só O.S em aberto
  const abertas = rows.filter(
    (r) => normCode(r.CONDICAOOS) === 'AB'
  );

  return abertas;
}

// Monta um mapa: PREFIXO -> { status, numeroOs }
function construirMapaOsPorVeiculo(rows) {
  const map = {};
  (rows || []).forEach((r) => {
    const prefixo = norm(r.PREFIXO);
    if (!prefixo) return;

    const numeroOs = norm(r.NUMERO_OS);

    if (!map[prefixo]) {
      map[prefixo] = {
        status: 'O.S em Aberto',
        numeroOs: numeroOs || 'Não informado',
      };
    }
  });
  return map;
}

// ===== Componente de recentralização do mapa =====
function RecenterMap({ center }) {
  const map = useMap();
  React.useEffect(() => {
    if (center && center.lat && center.lng) {
      map.setView(center, map.getZoom(), {
        animate: true,
      });
    }
  }, [center, map]);
  return null;
}

// ===== Componente principal =====
export default function MapaVeiculos() {
  const theme = useTheme();

  const [veiculos, setVeiculos] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [lastUpdated, setLastUpdated] = React.useState(null);
  const [secondsToRefresh, setSecondsToRefresh] = React.useState(
    REFRESH_SECONDS
  );

  const [anchorGaragemEl, setAnchorGaragemEl] = React.useState(null);
  const [mapStyle, setMapStyle] = React.useState('rua'); // 'rua' ou 'satelite'
  const [osPorVeiculo, setOsPorVeiculo] = React.useState({});


  const controllerRef = React.useRef(null);

  // Função de carregamento com AbortController para evitar corridas
  const loadVeiculos = React.useCallback(async () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      setLoading(true);
      setError('');
      const lista = await fetchPosicoesVeiculos({
        signal: controller.signal,
      });
      setVeiculos(lista);
      setLastUpdated(new Date());
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error(err);
      setError(
        err.message ||
          'Erro ao carregar posição dos veículos. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Carrega ao montar e configura auto-refresh com contador
  React.useEffect(() => {
    let intervalId;

    loadVeiculos();
    setSecondsToRefresh(REFRESH_SECONDS);

    intervalId = setInterval(() => {
      setSecondsToRefresh((prev) => {
        if (prev <= 1) {
          // Momento de atualizar
          loadVeiculos();
          return REFRESH_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      if (intervalId) clearInterval(intervalId);
    };
  }, [loadVeiculos]);

  // Carrega uma vez as O.S em aberto para cruzar com os veículos na garagem
  React.useEffect(() => {
    let abort = false;

    (async () => {
      try {
        const abertas = await fetchServicosAbertos();
        if (!abort) {
          setOsPorVeiculo(
            construirMapaOsPorVeiculo(abertas)
          );
        }
      } catch (err) {
        console.error(
          'Erro ao carregar O.S em aberto:',
          err
        );
      }
    })();

    return () => {
      abort = true;
    };
  }, []);

  // Filtro por veículo (código ou placa)
  const filteredVeiculos = React.useMemo(() => {
    const term = norm(searchTerm).toLowerCase();
    if (!term) return veiculos;

    return veiculos.filter((v) => {
      const codigo = v.codigo.toLowerCase();
      const placa = v.placa.toLowerCase();
      return (
        codigo.includes(term) ||
        placa.includes(term)
      );
    });
  }, [veiculos, searchTerm]);

  const veiculosGaragem = React.useMemo(() => {
    if (!veiculos || veiculos.length === 0) return [];

    const centerGarage = findGarageCenter(veiculos);
    if (!centerGarage) return [];

    return veiculos.filter((v) => {
      if (
        v.latitude == null ||
        v.longitude == null ||
        v.velocidade !== 0
      ) {
        return false;
      }
      const dist = distanceMeters(
        centerGarage.lat,
        centerGarage.lng,
        v.latitude,
        v.longitude
      );
      return dist <= 250; // metros ao redor da garagem
    });
  }, [veiculos]);

  // Centro do mapa: se houver filtro, centraliza no primeiro veículo filtrado
  const center = React.useMemo(() => {
    const base =
      filteredVeiculos[0] || veiculos[0];

    if (base && base.latitude != null && base.longitude != null) {
      return {
        lat: base.latitude,
        lng: base.longitude,
      };
    }
    return MANAUS_CENTER;
  }, [filteredVeiculos, veiculos]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleManualRefresh = () => {
    loadVeiculos();
    setSecondsToRefresh(REFRESH_SECONDS);
  };

  const openGaragem = Boolean(anchorGaragemEl);
  const handleOpenGaragem = (event) => {
    setAnchorGaragemEl(event.currentTarget);
  };
  const handleCloseGaragem = () => {
    setAnchorGaragemEl(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Cabeçalho e controles */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Mapa de Veículos — Tempo Real
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Visualize a posição atual dos veículos e
              filtre pelo número do carro (código) ou
              placa.
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <TextField
              size="small"
              label="Pesquisar veículo"
              placeholder="Ex: 9422"
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenGaragem}
              disabled={veiculosGaragem.length === 0}
            >
              Garagem ({veiculosGaragem.length})
            </Button>
            <ButtonGroup
              variant="outlined"
              size="small"
            >
              <Button
                onClick={() => setMapStyle('rua')}
                variant={
                  mapStyle === 'rua'
                    ? 'contained'
                    : 'outlined'
                }
              >
                Mapa
              </Button>
              <Button
                onClick={() => setMapStyle('satelite')}
                variant={
                  mapStyle === 'satelite'
                    ? 'contained'
                    : 'outlined'
                }
              >
                Satélite
              </Button>
            </ButtonGroup>

            <Tooltip title="Atualizar agora">
              <span>
                <IconButton
                  onClick={handleManualRefresh}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress
                      size={20}
                    />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <Box sx={{ minWidth: 120 }}>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Atualização automática
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
              >
                em {secondsToRefresh}s
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Info de última atualização */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Veículos exibidos:{' '}
            <strong>
              {filteredVeiculos.length}/
              {veiculos.length}
            </strong>
            {lastUpdated && (
              <>
                {' '}
                — Última atualização:{' '}
                {formatDateTimeBR(
                  lastUpdated.getTime()
                )}
              </>
            )}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        <Popover
          open={openGaragem}
          anchorEl={anchorGaragemEl}
          onClose={handleCloseGaragem}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 2, maxHeight: 400, maxWidth: 360 }}>
            <Typography variant="subtitle1" gutterBottom>
              Veículos na garagem ({veiculosGaragem.length})
            </Typography>

            {veiculosGaragem.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Nenhum veículo identificado na garagem.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Veículo</TableCell>
                    <TableCell align="center">Linha</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">N° O.S</TableCell>
                    <TableCell align="center">
                      Veloc.
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {veiculosGaragem.map((v) => {
                    const chave = v.codigo || v.placa || '';
                    const infoOs =
                      (chave && osPorVeiculo[chave]) || null;

                    return (
                      <TableRow
                        key={v.codigo || v.placa}
                      >
                        <TableCell align="center">
                          {v.codigo ||
                            v.placa ||
                            '—'}
                        </TableCell>
                        <TableCell align="center">
                          {v.linha || '—'}
                        </TableCell>
                        <TableCell align="center">
                          {infoOs
                            ? 'O.S em Aberto'
                            : 'Nada Registrado'}
                        </TableCell>
                        <TableCell align="center">
                          {infoOs
                            ? infoOs.numeroOs
                            : 'Não Registrado'}
                        </TableCell>
                        <TableCell align="center">
                          {v.velocidade.toFixed(0)} km/h
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>


              </Table>
            )}
          </Box>
        </Popover>

        {/* Container do mapa */}
        <Box
          sx={{

            mt: 1,
            height: { xs: 500, md: 650 },
            borderRadius: 3,
            overflow: 'hidden',
            border:
              '1px solid ' +
              theme.palette.divider,
          }}
        >

          <MapContainer
            center={center}
            zoom={13}
            style={{
              width: '100%',
              height: '100%',
            }}
            zoomControl={true}
          >
            <RecenterMap center={center} />

            <TileLayer
              attribution={
                mapStyle === 'rua'
                  ? '&copy; OpenStreetMap'
                  : 'Tiles &copy; Esri World Imagery'
              }
              url={
                mapStyle === 'rua'
                  ? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                  : 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
              }
            />


            {filteredVeiculos.map((v) => (
              <Marker
                key={
                  v.codigo ||
                  v.placa ||
                  `${v.latitude},${v.longitude}`
                }
                position={[
                  v.latitude,
                  v.longitude,
                ]}
                icon={createBusIcon(v.velocidade > 0)}
              >

                <LeafletTooltip
                  direction="top"
                  offset={[0, -30]}
                  opacity={0.9}
                >
                  <span>
                    Veículo:{' '}
                    {v.codigo ||
                      v.placa ||
                      '—'}
                    <br />
                    Linha:{' '}
                    {v.linha || '—'}
                  </span>
                </LeafletTooltip>

                <Popup>


                  <Box
                    sx={{
                      minWidth: 200,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                    >
                      <RoomIcon
                        fontSize="small"
                        sx={{ mr: 0.5 }}
                      />
                      Veículo{' '}
                      <strong>
                        {v.codigo ||
                          v.placa ||
                          '—'}
                      </strong>
                    </Typography>
                    {v.placa && (
                      <Typography
                        variant="body2"
                      >
                        Placa:{' '}
                        <strong>
                          {v.placa}
                        </strong>
                      </Typography>
                    )}
                    {v.linha && (
                      <Typography
                        variant="body2"
                      >
                        Linha:{' '}
                        <strong>
                          {v.linha}
                        </strong>
                      </Typography>
                    )}
                    {v.trajeto && (
                      <Typography
                        variant="body2"
                      >
                        Trajeto:{' '}
                        <strong>
                          {v.trajeto}
                        </strong>
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                    >
                      Sentido:{' '}
                      <strong>
                        {v.sentido ||
                          '—'}
                      </strong>
                    </Typography>
                    <Typography
                      variant="body2"
                    >
                      Velocidade:{' '}
                      <strong>
                        {v.velocidade.toFixed(
                          0
                        )}{' '}
                        km/h
                      </strong>
                    </Typography>
                    <Typography
                      variant="body2"
                    >
                      Ignição:{' '}
                      <strong>
                        {v.ignicao === 1
                          ? 'Ligada'
                          : 'Desligada'}
                      </strong>
                    </Typography>
                    <Typography
                      variant="body2"
                    >
                      Última posição:{' '}
                      <strong>
                        {formatDateTimeBR(
                          v.dataHora
                        ) || '—'}
                      </strong>
                    </Typography>
                  </Box>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>
      </Paper>
    </Box>
  );
}
